import { useEffect } from "react";

/**
 * Imperative <head> manager — no external dependency.
 * Sets: title, description, canonical, robots directives, Open Graph
 * (title/description/image/url/type/site_name), Twitter card,
 * and injects JSON-LD structured data (removed on unmount).
 *
 * All values are optional. Undefined values fall back to site-wide defaults
 * from index.html.
 */
const SITE_NAME = "VR Classes";
const DEFAULT_OG_IMAGE = "https://images.unsplash.com/photo-1637589316488-6d4c41b335cd?w=1200&q=80";

function upsertMeta(attr, key, value) {
  if (value == null || value === "") return null;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return el;
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function getBaseUrl() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

export default function Seo({
  title,
  description,
  canonical,        // full URL preferred; if a path, will be prefixed with origin
  image,            // Open Graph image
  noindex = false,
  type = "website", // or "article"
  jsonLd,           // object | array | null
  articleMeta,      // { published_at, updated_at, author, tags: [] }
}) {
  useEffect(() => {
    const base = getBaseUrl();
    const canonicalUrl = canonical
      ? (canonical.startsWith("http") ? canonical : base + canonical)
      : (base + window.location.pathname);
    const ogImg = image || DEFAULT_OG_IMAGE;
    const suffix = ` | ${SITE_NAME}`;
    const hasSuffix = title && (title.endsWith(SITE_NAME) || title.endsWith(suffix.trim()));
    const finalTitle = title
      ? (hasSuffix ? title : `${title}${suffix}`)
      : `${SITE_NAME} — Bihar Board Class 9-12 Notes, MCQ & YouTube Classes`;
    const finalDesc = description || "बिहार बोर्ड कक्षा 9-12 के लिए निःशुल्क Notes, MCQ Quiz, महत्वपूर्ण प्रश्न, Online Tests एवं YouTube Classes।";

    document.title = finalTitle;
    upsertMeta("name", "description", finalDesc);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertLink("canonical", canonicalUrl);

    // Open Graph
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDesc);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", ogImg);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "hi_IN");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDesc);
    upsertMeta("name", "twitter:image", ogImg);

    // Article metadata (for blog posts / notes)
    const articleTags = [];
    if (type === "article" && articleMeta) {
      if (articleMeta.published_at) articleTags.push(upsertMeta("property", "article:published_time", articleMeta.published_at));
      if (articleMeta.updated_at) articleTags.push(upsertMeta("property", "article:modified_time", articleMeta.updated_at));
      if (articleMeta.author) articleTags.push(upsertMeta("property", "article:author", articleMeta.author));
      (articleMeta.tags || []).forEach((t, i) => {
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", t);
        document.head.appendChild(el);
        articleTags.push(el);
      });
    }

    // JSON-LD (structured data). We tag scripts so we can clean them up on unmount.
    const ldNodes = [];
    const items = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    for (const obj of items) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-vr-jsonld", "1");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      ldNodes.push(s);
    }

    return () => {
      ldNodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
      articleTags.forEach((n) => n && n.parentNode && n.parentNode.removeChild(n));
    };
  }, [title, description, canonical, image, noindex, type, JSON.stringify(jsonLd), JSON.stringify(articleMeta)]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

/* ---------- helpers to build common JSON-LD ---------- */
export function orgLd() {
  const base = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: SITE_NAME,
    url: base,
    logo: base + "/favicon.ico",
    sameAs: ["https://youtube.com/@vrclassesfaizsir"],
  };
}

export function websiteLd() {
  const base = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: base,
    potentialAction: {
      "@type": "SearchAction",
      target: `${base}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbLd(items) {
  const base = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url ? (it.url.startsWith("http") ? it.url : base + it.url) : undefined,
    })),
  };
}

export function articleLd({ title, description, url, image, author, published, modified }) {
  const base = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image: image || DEFAULT_OG_IMAGE,
    author: { "@type": "Person", name: author || "VR Classes Team" },
    publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: base + "/favicon.ico" } },
    datePublished: published,
    dateModified: modified || published,
    mainEntityOfPage: url && (url.startsWith("http") ? url : base + url),
  };
}

export function blogPostLd(props) {
  return { ...articleLd(props), "@type": "BlogPosting" };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (faqs || []).map((f) => ({
      "@type": "Question",
      name: f.q || f.question,
      acceptedAnswer: { "@type": "Answer", text: f.a || f.answer },
    })),
  };
}
